import { useSearchParams } from 'react-router-dom';
import { ArrowUpFromLine } from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { ExportCsvButton } from '@/client/components/admin/Payment/ExportCsvButton';
import { WalletWithdrawals } from './WalletPanels';

const SCOPES = [
  { id: 'wallet', label: 'Wallet withdrawals' },
  { id: 'store', label: 'Store payouts' },
  { id: 'all', label: 'All' },
] as const;
type Scope = (typeof SCOPES)[number]['id'];

const PayoutsPage = () => {
  useBreadcrumbs([{ label: 'Billing & Payments', href: '/admin/payments/overview' }, { label: 'Payouts' }]);
  const adjust = useHasPermission('payment.adjust');
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('scope');
  const scope: Scope = SCOPES.some((s) => s.id === raw) ? (raw as Scope) : 'wallet';

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div>
          <div className="mb-1 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              <ArrowUpFromLine className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-2xl font-black text-gray-900">Payouts</h1>
          </div>
          <p className="text-sm text-gray-500">
            Money leaving the platform. Wallet withdrawals are resolved here; store payouts are
            shown for visibility and managed from each store&apos;s page.
          </p>
        </div>

        <div className="flex gap-1 border-b border-gray-200" role="tablist">
          {SCOPES.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={scope === s.id}
              onClick={() => setSearchParams(s.id === 'wallet' ? {} : { scope: s.id }, { replace: true })}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                scope === s.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <ExportCsvButton
          path="/api/admin/v2/wallets/payouts"
          params={{ scope }}
          filename={`payouts-${scope}.csv`}
        />

        <WalletWithdrawals
          key={scope}
          canAct={adjust.isSuperAdmin || adjust.hasPermission}
          scope={scope}
        />
      </div>
    </div>
  );
};

export default PayoutsPage;
